import type { GlobalSearchSuggestion } from './types.ts';
import clsx from 'clsx';
import { PiMagnifyingGlass } from 'react-icons/pi';
import { FormattedMessage, useIntl } from 'react-intl';
import { SharedConfig } from '@_/config/shared-config.ts';
import { buttonCommonI18n } from '@_/i18n-common';
import { Autocomplete, AutocompleteBackdrop, AutocompleteInput, Button } from '@_/ui';
import { placeholderI18n } from '#src/i18nMessages/index.ts';
import { GlobalSearchOptions } from './GlobalSearchOptions.tsx';
import { useGlobalSearch } from './useGlobalSearch.ts';

export function GlobalSearch({
  openWidth,
  showSubmitButton = true,
  size = 'md'
}: {
  openWidth?: number;
  showSubmitButton?: boolean;
  size?: 'md' | 'lg';
}) {
  const intl = useIntl();

  const { autocompleteProps, onSubmit, popupRef } = useGlobalSearch();
  const { open, onOpenChange } = autocompleteProps;

  return (
    <Autocomplete<GlobalSearchSuggestion> {...autocompleteProps}>
      {open && <AutocompleteBackdrop className="fixed inset-0 z-50 bg-white/80" />}

      <div
        className={clsx('relative', size === 'md' && 'h-10', size === 'lg' && 'h-14')}
        style={{
          width: open && openWidth ? `${openWidth}px` : '100%'
        }}
      >
        <div
          className={clsx(
            'w-full overflow-hidden border bg-white px-3',
            open
              ? [
                  'absolute z-50 border-gray-200 shadow-lg',
                  size === 'md' && '-top-2 -left-1 rounded-md',
                  size === 'lg' && 'top-0 left-0 rounded-xl'
                ]
              : 'relative rounded-full border-gray-200'
          )}
        >
          <form className="block" onSubmit={onSubmit}>
            <AutocompleteInput
              render={inputProps => (
                <div
                  className={clsx(
                    'flex h-full items-center',
                    open
                      ? clsx(
                          'border-b-2 border-gray-600',
                          size === 'md' && 'pt-4 pb-2.5',
                          size === 'lg' && 'py-3'
                        )
                      : clsx(size === 'md' && 'py-2', size === 'lg' && 'py-3')
                  )}
                >
                  <div
                    aria-hidden="true"
                    className={clsx(
                      'flex text-lg text-gray-900',
                      open
                        ? clsx(size === 'md' && 'pl-1.5', size === 'lg' && 'pl-2')
                        : clsx(size === 'md' && 'pl-0.5', size === 'lg' && 'pl-2')
                    )}
                  >
                    <PiMagnifyingGlass className="size-5" />
                  </div>

                  <input
                    {...inputProps}
                    className={clsx(
                      'ml-3 p-0',
                      'grow resize-none appearance-none border-0 font-medium text-gray-950 placeholder-gray-400 placeholder:font-normal',
                      'focus:ring-0 focus:outline-hidden',
                      size === 'md' && 'text-sm',
                      size === 'lg' && 'text-base'
                    )}
                    maxLength={SharedConfig.maxLength.searchTerm}
                    placeholder={intl.formatMessage(placeholderI18n.globalSearch)}
                    type="text"
                  />
                </div>
              )}
            />

            {showSubmitButton && (
              <div className="absolute top-1.5 right-2">
                <Button
                  color={open ? 'primary' : 'primary'}
                  rounded
                  type="submit"
                  variant={open ? 'outline' : 'default'}
                >
                  <FormattedMessage {...buttonCommonI18n.search} />
                </Button>
              </div>
            )}
          </form>

          {open && (
            <div
              ref={popupRef}
              className="-mx-3 max-h-96 overflow-auto px-3 py-2 [--gutter-x:--spacing(3)]"
            >
              <GlobalSearchOptions
                items={autocompleteProps.items}
                onSelectItem={() => onOpenChange(false)}
              />
            </div>
          )}
        </div>
      </div>
    </Autocomplete>
  );
}
